import { capitalize, sortBy } from 'lodash';
import { connect } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import i18next from 'i18next';
import React, { useEffect, useMemo, useState } from 'react';

import {
  AccountContainer,
  Container,
  Icon,
  NameContainer,
  ScrollableAccountList,
  StyledLink,
  SwichContainer,
  textWithEllipsis,
  UserContainer,
} from './styledComponents';

import { logout } from '../../../actions/logout';

import SwitchAccountItem from './components/SwitchAccountItem';
import Text, { ENUM_COLORS, ENUM_FONTS } from '../../Text';

import {
  clearAppSessionStorage,
  isMosAuthProvider,
  setLegacyAuthProvider,
} from '../../auth/session';
import { setLocalStorageItem } from '../../utils/localStorage';

/** SERVICES */
import { startMosLogin } from '../../../services/mosAuth';
import { switchAccount } from '../../../services/user';
import { switchMosDefaultOperator } from '../../../services/mosSwitchers';

const HOME_PATH = '/home';

const UserSection = ({
  user,
  hasMosOperatorSwitcher,
  hasMultipleAccounts,
  isSwitchAccountOpened,
  mosOperators,
  setIsSwitchAccountOpened,
  triggerDrawer,
  handleLogout,
  updateReferential,
}) => {
  const history = useHistory();

  const userDetailPath = `/users/${user.id}/details`;

  const [groupOtherAccounts, setGroupOtherAccounts] = useState([]);
  const isAccountSwitchable = hasMultipleAccounts || hasMosOperatorSwitcher;

  useEffect(() => {
    if (!user || !hasMultipleAccounts) {
      return;
    }

    const { accountGroupMappings } = user;

    const otherAccounts = accountGroupMappings.filter(({ accountId }) => accountId !== user.id);

    setGroupOtherAccounts(sortBy(otherAccounts, 'clientName'));
  }, [user]);

  const otherMosOperators = useMemo(() => {
    if (!hasMosOperatorSwitcher) {
      return [];
    }

    return mosOperators.filter(({ id }) => id !== user.mosOpId);
  }, [hasMosOperatorSwitcher, mosOperators, user.mosOpId]);

  const logoutUser = () => {
    // Capture provider before any async logout work clears storage.
    const shouldClearLocalSession = !isMosAuthProvider();

    handleLogout();

    // MOS logout clears storage and redirects to Mapal OS end-session via logoutMos.
    // Legacy/SSO: ensure keys are cleared if the logout API path did not.
    if (shouldClearLocalSession) {
      clearAppSessionStorage();
    }
  };

  const handleSwitchAccount = async (targetAccountId) => {
    const targetAccount = await switchAccount(targetAccountId);

    setLocalStorageItem('loopbackAccessToken', targetAccount.id);
    setLocalStorageItem('userId', targetAccount.userId);
    setLegacyAuthProvider();

    // Change to Sales Point mode
    updateReferential(false);

    history.go(HOME_PATH);
  };

  /**
   * The API resolves the Account from the operator_id claim of the MOS access token, so
   * persisting the new default operator is not enough: the MOS authorize flow is re-run
   * (silent thanks to the MOS SSO cookie) to get a token for that operator and log into
   * the matching Account.
   */
  const handleSwitchMosOperator = async (operatorId) => {
    try {
      await switchMosDefaultOperator(operatorId);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[MOS] default operator switch failed:', error);
      return;
    }

    await startMosLogin({ returnUrl: HOME_PATH });
  };

  const renderCurrentAccount = () => (
    <>
      <NameContainer>
        <Text color={isSwitchAccountOpened ? ENUM_COLORS.INFO_GREEN : ENUM_COLORS.LIGHTEST}>
          {capitalize(user.firstName)} {capitalize(user.lastName)}
        </Text>
        <Text
          color={isSwitchAccountOpened ? ENUM_COLORS.INFO_GREEN : ENUM_COLORS.DGREY}
          font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}
        >
          {user.lnkClientAccountrel.name}
        </Text>
      </NameContainer>
    </>
  );

  return (
    <Container
      hasMultipleAccounts={isAccountSwitchable}
      isSwitchAccountOpened={isSwitchAccountOpened}
    >
      {!isAccountSwitchable ? (
        <>
          <UserContainer>
            <NameContainer>
              <Link to={userDetailPath} onClick={() => triggerDrawer({ closeDrawer: true })}>
                <Text color={ENUM_COLORS.LIGHTEST}>
                  {capitalize(user.firstName)} {capitalize(user.lastName)}
                </Text>
              </Link>
              <Link to={userDetailPath} onClick={() => triggerDrawer({ closeDrawer: true })}>
                <Text
                  color={ENUM_COLORS.DGREY}
                  font={ENUM_FONTS.TEXT_COMPONENT_LABEL}
                  style={textWithEllipsis}
                >
                  {user.lnkClientAccountrel.name}
                </Text>
              </Link>
            </NameContainer>
          </UserContainer>
          <Icon src={'/images/inpulse/logout.svg'} onClick={logoutUser} />
        </>
      ) : (
        <SwichContainer>
          <AccountContainer onClick={() => setIsSwitchAccountOpened(!isSwitchAccountOpened)}>
            {renderCurrentAccount()}
            <Icon
              src={
                isSwitchAccountOpened
                  ? '/images/inpulse/chevron-down-green-small.svg'
                  : '/images/inpulse/chevron-up-white-small.svg'
              }
              onClick={() => setIsSwitchAccountOpened(!isSwitchAccountOpened)}
            />
          </AccountContainer>
          {isSwitchAccountOpened && (
            <>
              <ScrollableAccountList>
                <StyledLink
                  to={userDetailPath}
                  onClick={() => triggerDrawer({ closeDrawer: true })}
                >
                  <AccountContainer>
                    <NameContainer>
                      <Text color={ENUM_COLORS.LIGHTEST}>{i18next.t('GENERAL.MY_ACCOUNT')}</Text>
                    </NameContainer>
                    <Icon src={'/images/inpulse/user-white-small.svg'} onClick={() => {}} />
                  </AccountContainer>
                </StyledLink>
                {otherMosOperators.map(({ id, name }) => (
                  <SwitchAccountItem
                    key={id}
                    name={name}
                    onClick={() => handleSwitchMosOperator(id)}
                  />
                ))}
                {groupOtherAccounts.map(({ accountId, clientName }) => (
                  <SwitchAccountItem
                    key={accountId}
                    name={clientName}
                    onClick={() => handleSwitchAccount(accountId)}
                  />
                ))}
              </ScrollableAccountList>

              <AccountContainer isBottom onClick={logoutUser}>
                <NameContainer>
                  <Text color={ENUM_COLORS.LIGHTEST}>
                    {i18next.t('GENERAL.NO_ASSOCIATED_STORE_DETECTED_ACTION')}
                  </Text>
                </NameContainer>
                <Icon src={'/images/inpulse/logout.svg'} onClick={logoutUser} />
              </AccountContainer>
            </>
          )}
        </SwichContainer>
      )}
    </Container>
  );
};

const mapStateToProps = (state) => ({
  user: state.baseReducer.user,
});

const mapDispatchToProps = (dispatch) => ({
  handleLogout: () => {
    dispatch(logout());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(UserSection);
